import React, { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import dayjs from "dayjs";
import {
  formatCurrencyAmount,
} from "../../../../../../utils/currency";
import { SuccessAlertContext } from "../../../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { useNavigate } from "react-router-dom";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { payoutRequestToAsiaWithDocuments, setLoading } from "../../../../redux/slice";
import { getCnyPayoutDocument, setCnyPayoutDocument } from "../asiaPayoutDocument";
import { clearAsiaPayoutData, setStep } from "../../../../redux/formDataSlice";
import { requestPayoutV2Otp } from "../../../../api";
import { getErrorMessage } from "../../../../utils";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsSectionHeader,
  DetailsValue,
  EditButton,
  SectionTitle,
} from "../../../shared/styles";
import Loader from "../../../../../../components/common/Loader";
import WalletOTP from "../../../../components/SendFunds/components/WalletOTP";
import { DetailsLabel } from "../../../../../Settlements/SettlementPayout/styles";
import { Button } from "../../../../../../components/common/Button";

interface AliPayReviewFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const AliPayReviewForm: React.FC<AliPayReviewFormProps> = ({ handlePrevious }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getSavedData: FormInputs = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});

  const [verifyOTP, setVerifyOTP] = useState(false);

  const { onShowAlert: onShowSuccessAlert } = useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } = useContext(NetworkErrorAlertContext) || {};

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;
  const sourceCurrency = getSavedData?.sourceCurrency;
  const currentDate = dayjs().format("DD.MM.YYYY");

  const senderName =
    `${getSavedData?.senderFirstName || ""} ${getSavedData?.senderLastName || ""}`.trim();

  const handlePayout = async (otp?: string) => {
    try {
      if (!businessId) {
        onShowErrorAlert?.("", "Business information is not available. Please try again.");
        return;
      }

      const rawPayload: any = {
        currency: "CNY",
        quotationId: getSavedData.quotationId,
        purposeCode: getSavedData.purposeCode,
        requestId: getSavedData.reference,
        otp: otp,
        name: getSavedData.accountHolderName,
        nationalIdentificationNumber: getSavedData.receiverIdNumber,
        phoneNumber: getSavedData.receiverMobileNumber,
        address: getSavedData.receiverStreetAddress,
        city: getSavedData.receiverCity,
        state: getSavedData.receiverState,
        country: getSavedData.receiverCountry,
        postalCode: getSavedData.receiverPostCode,
      };

      const senderDocument = getCnyPayoutDocument();
      if (!senderDocument) {
        onShowErrorAlert?.("", "Please upload the sender's ID again before sending.");
        return;
      }
      rawPayload.documents = [{ type: "id" }];

      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        rawPayload
      );
      const authToken = jitPublicKey || publicKey;

      const action = await dispatch(
        payoutRequestToAsiaWithDocuments({
          message: encryptedMessage,
          documents: [senderDocument],
          token: authToken,
        })
      );

      if (payoutRequestToAsiaWithDocuments.fulfilled.match(action)) {
        onShowSuccessAlert?.("Payout successful");
        setCnyPayoutDocument(null);
        dispatch(clearAsiaPayoutData({ country: "cn" }));
        navigate("/wallets");
      } else if (payoutRequestToAsiaWithDocuments.rejected.match(action)) {
        onShowErrorAlert?.("", (action.payload as string) || "Payout failed");
      }
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Payout failed"));
    }
  };

  const handleSendOTP = async () => {
    try {
      dispatch(setLoading(true));
      await requestPayoutV2Otp(getSavedData?.reference, publicKey);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Failed to send OTP"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEditStep = (stepIndex: number) => {
    dispatch(setStep(stepIndex));
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP
          setVerifyOTP={setVerifyOTP}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handleEditStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary receives</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("CNY", getSavedData?.amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>
              1 {sourceCurrency} = {getSavedData?.rate} CNY
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.fee)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>{getSavedData?.description || getSavedData?.purpose}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Payment method</DetailsLabel>
            <DetailsValue>AliPay</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Sender details</SectionTitle>
          <EditButton onClick={() => handleEditStep(1)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Sender type</DetailsLabel>
            <DetailsValue>{getSavedData?.senderType}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender name</DetailsLabel>
            <DetailsValue>{senderName}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary details</SectionTitle>
          <EditButton onClick={() => handleEditStep(2)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{getSavedData?.accountHolderName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>National ID</DetailsLabel>
            <DetailsValue>{getSavedData?.receiverIdNumber || "-"}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Phone number</DetailsLabel>
            <DetailsValue>{getSavedData?.receiverMobileNumber || "-"}</DetailsValue>
          </DetailsRow>
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSendOTP} label="Confirm" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default AliPayReviewForm;
